import { Button, ConfigProvider, Tooltip } from 'antd';
import DraggableModal from '../DraggableModal';
import { useTimer } from '../Timer/useTimer';
import { blue, red } from '@ant-design/colors';
import { ClockCircle } from '../../../assets/CustomIcons/ClockCircle';
import { CheckCircle } from '../../../assets/CustomIcons/CheckCircle';
import { useCallback, useContext, useState } from 'react';
import { useTimerStore } from '../timerStore';
import { useAgenda } from '../Agenda/Agenda';
import { handleFinishPlanningContext } from '../PlanningSession';
import { getCurrentWorkspaceActiveSprint } from '../../../serverState/sprint';
import CustomTooltip from '../../UI/Tooltip/CustomTooltip';
import { BaseButton } from '../../UI/BaseButton';
import { ConfirmStartSprintModal } from '../../Modal/ConfirmStartSprintModal';

interface PlanningHeaderProps {
  hasPlanningTasks: boolean;
}

export const PlanningHeader: React.FC<PlanningHeaderProps> = ({ hasPlanningTasks }) => {
  const [openTimer, setOpenTimer] = useState<boolean>(false);
  const [openAgenda, setOpenAgenda] = useState<boolean>(false);
  const [openStartSprintConfirm, setOpenStartSprintConfirm] = useState<boolean>(false);

  const { renderTimer } = useTimer();

  const stopwatchData = useTimerStore((state) => state.stopwatchData);
  const timerConfig = useTimerStore((state) => state.timerConfig);

  const TimerDigits = useCallback(() => {
    if (timerConfig?.status === 'active') {
      return (
        <div className={' font-bold text-center'}>
          {stopwatchData.minutes < 10 ? `0${stopwatchData.minutes}` : stopwatchData.minutes}:
          {stopwatchData.seconds < 10 ? `0${stopwatchData.seconds}` : stopwatchData.seconds}
        </div>
      );
    } else {
      return <div className="text-sm font-medium leading-none">Timer</div>;
    }
  }, [stopwatchData]);

  const { renderAgenda, checked } = useAgenda();

  const { handleFinishPlanning } = useContext(handleFinishPlanningContext);

  const activeSprintQuery = getCurrentWorkspaceActiveSprint();

  const openTimerModal = (open: boolean) => {
    setOpenTimer(open);
    if (open && openAgenda) {
      setOpenAgenda(false);
    }
  };

  const openAgendaModal = (open: boolean) => {
    setOpenAgenda(open);
    if (open && openTimer) {
      setOpenTimer(false);
    }
  };

  return (
    <div className="planning-header-buttons flex gap-2">
      <ConfigProvider
        theme={{
          components: {
            Button: {
              colorPrimary: blue.primary,
            },
          },
        }}
      >
        <ConfigProvider
          theme={{
            components: {
              Button: {
                colorPrimary: stopwatchData.minutes === 0 && stopwatchData.seconds === 0 ? red.primary : blue.primary,
              },
            },
          }}
        >
          <DraggableModal
            initialPositionStyles={{ top: 182, right: 12 }}
            modalContent={<>{renderTimer}</>}
            modalDragTitle={'Timer'}
            renderButton={(open: boolean) => (
              <span className="!flex items-center gap-2">
                <ClockCircle />
                <TimerDigits />
              </span>
            )}
            open={openTimer}
            setOpen={openTimerModal}
          />
        </ConfigProvider>

        {/* <DraggableModal
          modalContent={<>{renderAgenda}</>}
          initialPositionStyles={{ top: 182, right: 12 }}
          modalDragTitle={'Checklist'}
          renderButton={(open: boolean) => (
            <span className="!flex items-center gap-2">
              <CheckCircle />
              {checked.length ? `${checked.length}/4` : 'Checklist'}
            </span>
          )}
          open={openAgenda}
          setOpen={openAgendaModal}
        /> */}
      </ConfigProvider>

      <BaseButton
        type="primary"
        size="medium"
        onClick={() => setOpenStartSprintConfirm(true)}
        disabled={!!activeSprintQuery.data || !hasPlanningTasks}
        disabledTooltip={
          activeSprintQuery.data
            ? 'End sprint to start the next one'
            : hasPlanningTasks
              ? undefined
              : 'Sprint can not be started if no tasks selected'
        }
      >
        Start Sprint
      </BaseButton>

      <ConfirmStartSprintModal
        isOpen={openStartSprintConfirm}
        onCancel={() => setOpenStartSprintConfirm(false)}
        onConfirm={() => {
          setOpenStartSprintConfirm(false);
          handleFinishPlanning?.();
        }}
      />
    </div>
  );
};
